import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, EmptyState, Surface } from '@vst/beam';
import { Alert } from '@vst/beam-icons/illustrative-icons';
import { useTranslation } from 'react-i18next';

import { openInNewTab, selectLocale, selectUserInfo } from '@app/redux';
import { selectConfig } from '@app/redux/config';

import { AccountType, Locale } from '@mfe/shared/schema-types';

import { AEMContentProvider, AemFeature, createAemUrl } from '../AdobeExperienceFragments';

import styles from './styles.module.scss';

export const CommonQuestions = ({ feature }: { feature: AemFeature }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('Global');

  const [aemFragmentLoading, setAEMFragmentLoading] = useState(true);
  const [aemFragmentError, setAEMFragmentError] = useState(false);

  const { aemBaseUrl, loadingConfig } = useSelector(selectConfig);

  const {
    loading: loadingUserInfo,
    userInfo: { accountType },
  } = useSelector(selectUserInfo);

  const {
    locale: { userLocale },
  } = useSelector(selectLocale);

  const loading = loadingConfig || loadingUserInfo;

  const aemURL = createAemUrl({
    baseUrl: aemBaseUrl,
    locale: userLocale,
    feature,
    accountType,
  });

  const openLinkInNewTab = (e: React.MouseEvent<HTMLElement>, url: string) => {
    e.preventDefault();
    dispatch(openInNewTab(url));
  };

  const navigateToFaq = (e: React.MouseEvent<HTMLElement>) => {
    const smbHelpUrl = t('SMBSupportLink');
    const residentialHelpUrl = t('viasatHelpLink');
    const isSmb = accountType === AccountType.Smb;
    const helpUrl = isSmb ? smbHelpUrl : residentialHelpUrl;
    openLinkInNewTab(e, helpUrl);
  };

  if (loading) {
    return <QuestionsLoading />;
  }

  if (aemFragmentError && !aemFragmentLoading && !loading) {
    return <CommonQuestionsError />;
  }

  const showFAQButton = userLocale === Locale.EnUs && !aemFragmentLoading && !aemFragmentError;

  return (
    <>
      <AEMContentProvider
        src={aemURL}
        onLoad={() => setAEMFragmentLoading(false)}
        onError={() => {
          setAEMFragmentError(true);
        }}
      />
      {showFAQButton && (
        <Button fluid variant="secondary" mt="16px" onClick={(e) => navigateToFaq(e)}>
          {t('viewFaqs')}
        </Button>
      )}
    </>
  );
};

const QuestionsLoading = () => {
  return (
    <div className={styles['loadingContainer']}>
      <div className={styles['loadingTitle']} />
      <div className={styles['loadingCell']} />
      <div className={styles['loadingCell']} />
      <div className={styles['loadingCell']} />
      <div className={styles['loadingButton']} />
    </div>
  );
};

const CommonQuestionsError = () => {
  const { t } = useTranslation('Global');
  return (
    <Surface variant="secondary" py={'40px'} radius="16px" className={styles['commonQuestionsError']}>
      <EmptyState icon={Alert} title={t('CommonQuestionsError')} />
    </Surface>
  );
};
